import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useAuth } from '../contexts/AuthContext';
import { getHomeRouteByRole } from '../lib/rbac';
import { workspaceAdminAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';

/**
 * Move between the locations of one organisation — head office and its branches.
 *
 * Renders nothing unless the server says this account may switch and there is
 * somewhere to switch to. That decision is deliberately the server's: a company
 * with one location gets no menu, and an operator proxy session gets none either
 * (switching from inside one would be a way around the consent gate that
 * proxy-login enforces).
 *
 * Selecting your own location is an *exit*, not a switch, so it goes through
 * exitProxy and drops the borrowed session rather than nesting another one.
 */
const LocationSwitcher = () => {
  const { user, switchLocation, exitProxy } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await workspaceAdminAPI.listMyLocations();
      setState(data || null);
    } catch {
      // A workspace with no locations, or an older server. Either way: no menu,
      // and never an error toast on a page that has nothing to do with this.
      setState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-read after every switch: the current location has changed, and so has
  // which row should be ticked.
  useEffect(() => { load(); }, [load, user?.tenantId]);

  const items = useMemo(() => (Array.isArray(state?.items) ? state.items : []), [state?.items]);

  const onChange = useCallback(async (slug) => {
    if (!slug || slug === state?.current?.slug) return;
    setSwitching(true);
    try {
      const goingHome = slug === state?.home?.slug;
      const response = goingHome && state?.away_from_home
        ? await exitProxy()
        : await switchLocation(slug);
      // Land on the role's own home route. The route we were on belongs to the
      // previous location's session and may not even exist in this one.
      navigate(getHomeRouteByRole(response?.role), { replace: true });
      const name = items.find((row) => row.slug === slug)?.name || slug;
      toast.success(goingHome ? `Back at ${name}` : `Now viewing ${name}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not switch location'));
    } finally {
      setSwitching(false);
    }
  }, [state, items, switchLocation, exitProxy, navigate]);

  if (loading && !state) return null;
  if (!state?.can_switch || items.length < 2) return null;

  return (
    <Select value={state.current?.slug || undefined} onValueChange={onChange} disabled={switching}>
      <SelectTrigger className="h-9 w-[160px] sm:w-[200px] md:w-[230px]" aria-label="Location">
        <span className="flex items-center gap-1.5 min-w-0">
          {switching
            ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
            : <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
          <SelectValue placeholder="Location" />
        </span>
      </SelectTrigger>
      <SelectContent>
        {items.map((row) => (
          <SelectItem key={`location-${row.tenant_id}`} value={row.slug}>
            <span className="flex items-center gap-2">
              <span>{row.name}</span>
              <span className="text-[11px] text-muted-foreground">
                {row.kind === 'head_office' ? 'Head office' : 'Branch'}
                {row.is_home ? ' · yours' : ''}
                {typeof row.employee_count === 'number' ? ` · ${row.employee_count}` : ''}
              </span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default LocationSwitcher;
